/**
 * ZONE REGION DIALOG — Aggiunge / modifica una provincia della zona (FILO-29).
 *
 * Colore e commerciale sono facoltativi: senza, valgono quelli della zona
 * (`color: ''`, `liferayUserId: 0`). Una provincia sta in una sola zona del
 * sito: quelle già prese sono disabilitate. In modifica il PATCH invia solo i
 * campi cambiati, e «Togli dalla zona» cancella la provincia.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { zoneApi } from '../../entities/zone/zoneApi';
import { extractError } from '../../store/thunkHelpers';
import type { Province, Zone, ZoneRegion, ZoneRegionPayload, ZoneUser } from '../../entities/zone/zoneTypes';
import { useToast } from '../useToast';
import { ColorPicker } from '../ColorPicker';

const HEX6 = /^#[0-9a-f]{6}$/i;

interface Props {
  zone: Zone;
  /** Provincia in modifica; assente = nuova. */
  region?: ZoneRegion;
  /** Tutte le zone del sito: le loro province sono già prese. */
  zones: Zone[];
  onClose: () => void;
  onSaved: () => void;
}

export function ZoneRegionDialog({ zone, region, zones, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const [provinces, setProvinces] = useState<Province[] | null>(null);
  const [users, setUsers] = useState<ZoneUser[]>([]);
  const [regionId, setRegionId] = useState<number>(region?.regionId ?? 0);
  const [ownColor, setOwnColor] = useState(!!region?.color);
  const [color, setColor] = useState(region?.color || zone.color);
  // In modifica il commerciale attuale è un'opzione a sé: può non essere più fra gli utenti attivi.
  const current: ZoneUser | null = region && region.liferayUserId > 0
    ? { userId: region.liferayUserId, fullName: region.liferayUserName || `#${region.liferayUserId}` }
    : null;
  const [user, setUser] = useState<ZoneUser | null>(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([zoneApi.provinces(), zoneApi.users()])
      .then(([p, u]) => {
        // In modifica la provincia attuale resta fra le opzioni anche se non è più offerta.
        const missing = region && !p.some((x) => x.regionId === region.regionId);
        setProvinces(missing ? [...p, { regionId: region.regionId, regionCode: region.regionCode, name: region.regionName }] : p);
        setUsers(u);
      })
      .catch((err) => {
        toast({ severity: 'error', message: extractError(err) });
        onClose();
      });
    // Si carica all'apertura: `toast` e `onClose` non fanno parte della chiave.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Province prese da altre zone (o da altre righe di questa), con il nome della zona che le ha.
  const taken = new Map<number, string>();
  for (const z of zones) {
    for (const r of z.regions) if (r.zoneRegionId !== region?.zoneRegionId) taken.set(r.regionId, z.name);
  }
  const userOptions = current && !users.some((u) => u.userId === current.userId) ? [current, ...users] : users;
  const validColor = !ownColor || HEX6.test(color);
  const newColor = ownColor ? color.toLowerCase() : '';
  const userId = user?.userId ?? 0;
  const changed = !region || regionId !== region.regionId || newColor !== region.color || userId !== region.liferayUserId;
  const canSave = regionId > 0 && validColor && changed && !saving;

  async function run(action: () => Promise<unknown>) {
    setSaving(true);
    try {
      await action();
      onSaved();
    } catch (err) {
      toast({ severity: 'error', message: extractError(err) });
      setSaving(false);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    if (!region) {
      void run(() => zoneApi.addRegion(zone.zoneId, { regionId, color: newColor, liferayUserId: userId }));
      return;
    }
    const patch: ZoneRegionPayload = {};
    if (regionId !== region.regionId) patch.regionId = regionId;
    if (newColor !== region.color) patch.color = newColor;
    if (userId !== region.liferayUserId) patch.liferayUserId = userId;
    void run(() => zoneApi.updateRegion(region.zoneRegionId, patch));
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="tc-head">
        <span className="tc-kicker">
          {t(region ? 'settings.zones.regions.edit' : 'settings.zones.regions.add', { zone: zone.name })}
        </span>
        <IconButton aria-label={t('common.cancel')} onClick={onClose} size="small" className="tc-x">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent className="app-dialog__body">
          <TextField
            select
            label={t('settings.zones.fields.province')}
            // Finché le province non sono caricate l'opzione non esiste: valore vuoto.
            value={provinces && regionId ? regionId : ''}
            onChange={(e) => setRegionId(Number(e.target.value))}
            size="small"
            fullWidth
            required
            className="form-field"
          >
            {(provinces ?? []).map((p) => (
              <MenuItem key={p.regionId} value={p.regionId} disabled={taken.has(p.regionId)}>
                {p.name} · {p.regionCode}
                {taken.has(p.regionId) && ` — ${taken.get(p.regionId)}`}
              </MenuItem>
            ))}
          </TextField>

          <FormControlLabel
            control={<Checkbox checked={ownColor} onChange={(e) => setOwnColor(e.target.checked)} size="small" />}
            label={t('settings.zones.regions.ownColor')}
          />
          {ownColor && (
            <div className="zone-settings__color">
              <ColorPicker
                label={t('settings.zones.fields.color')}
                value={HEX6.test(color) ? color.toLowerCase() : zone.color}
                onChange={setColor}
              />
              <TextField
                label={t('settings.zones.fields.color')}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                size="small"
                fullWidth
                required
                error={!validColor}
                className="form-field"
                slotProps={{ htmlInput: { maxLength: 7 } }}
              />
            </div>
          )}

          <Autocomplete
            options={userOptions}
            value={user}
            onChange={(_, v) => setUser(v)}
            getOptionLabel={(o) => o.fullName}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            noOptionsText={t('settings.zones.noUsers')}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('settings.zones.fields.salesperson')}
                placeholder={t('settings.zones.regions.zoneSalesperson', { name: zone.liferayUserName || '—' })}
                size="small"
                className="form-field"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" variant="outlined" onClick={onClose}>{t('common.cancel')}</Button>
          {region && (
            <Button
              type="button"
              variant="outlined"
              disabled={saving}
              onClick={() => void run(() => zoneApi.removeRegion(region.zoneRegionId))}
            >
              {t('settings.zones.regions.remove')}
            </Button>
          )}
          <Button type="submit" variant="contained" disabled={!canSave}>{t('common.save')}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
